import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ViewChild } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { Table } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { NgShuiModule } from '@shui/angular';
import { SortEvent } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { catchError, forkJoin, map, merge, of, switchMap, tap } from 'rxjs';
import { AppConfigService } from 'src/core/services/appconfig.service';
import { AuthService } from 'src/core/services/auth.service';
import { BookmarkService } from 'src/core/services/bookmark.service';
import { BusinessPartnerAccessService } from 'src/core/services/business-partner-access.service';
import { NavigationService } from 'src/core/services/navigation.service';
import { ChatService } from 'src/core/services/chat.service';
import { DataService } from 'src/core/services/data.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedSiteService } from 'src/core/services/selectedsite.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { WidgetService } from 'src/core/services/widget.service';
import { BookmarkDeviceResponse, BookmarkSiteResponse } from 'src/core/types/bookmarks.types';
import { ApiResponse } from 'src/core/types/general.types';
import { workareaSize } from 'src/core/types/workarea.types';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-bookmark-menu',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './bookmark-menu.component.html',
  styleUrl: './bookmark-menu.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BookmarkMenuComponent {

  constructor(
    private bookmarkService: BookmarkService,
    private authService: AuthService,
    private dataService: DataService,
    private chatService: ChatService,
    private widgetService: WidgetService,
    private appConfigService: AppConfigService,
    private selectedSiteService: SelectedSiteService,
    private selectedSystemService: SelectedsystemService,
    private daterangeService: DaterangeService,
    private navigationService: NavigationService,
    private bpAccessService: BusinessPartnerAccessService
  ) {
    this.navigationService.homeClicked$.subscribe(() => {
      this.resetFilter();
    });

    this.navigationService.bookmarkClicked$.subscribe(() => {
      this.resetFilter();
    });
  }

  @ViewChild('table') table: Table;
  @ViewChild('tableSites') tableSites: Table;
  selectedGroup: "sites" | "instruments" = "instruments";
  bookmarks: any[] = [];
  bookmarksSites: any[] = [];
  isLoadingBookmarks: boolean = true;
  isError: boolean = false;

  searchTermSerial: string = '';
  searchTermInstrument: string = '';
  searchTermFuncloc: string = '';

  searchTermName: string = '';
  searchTermAddress: string = '';
  searchTermCountry: string = '';

  workareaSize: workareaSize = "medium";
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = () => {
    this.widgetService.onMinimize();
    this.resetFilter();
  };
  onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("medium");
    }
  }
  selectedRange: number;
  Math = Math;

  get showMachineDataColumns(): boolean {
     if (!environment.businessPartnerMode) {
      return true;
    }

    return this.bpAccessService.canViewMachineData;
  }

  get showServiceDataColumns(): boolean {
    if (!environment.businessPartnerMode) {
      return true;
    }

    return this.bpAccessService.canViewServiceData;
  }

  get showInstrumentSiteColumn(): boolean {
    return this.showMachineDataColumns || this.showServiceDataColumns;
  }

  get instrumentTableColspan(): number {
    return 3
      + (this.showMachineDataColumns ? 4 : 0)
      + (this.showServiceDataColumns ? 1 : 0)
      + (this.showInstrumentSiteColumn ? 1 : 0);
  }

  get siteTableColspan(): number {
    return 4
      + (this.showMachineDataColumns ? 4 : 0)
      + (this.showServiceDataColumns ? 1 : 0);
  }

  get instrumentGlobalFilterFields(): string[] {
    const fields = ['serial', 'instrumentName'];

    if (this.showInstrumentSiteColumn) {
      fields.push('FUNCTIONAL_LOCATION');
    }

    return fields;
  }

  ngOnInit(): void {
    const accountId = this.authService.userData.basic["id"];

    // Load once on init, and again whenever another component triggers a refresh.
    // This avoids an initial "empty table" flash caused by the BehaviorSubject's initial [] emission.
    if(this.bpAccessService.canViewDeviceLocation || !environment.businessPartnerMode) {
    merge(of(void 0), this.bookmarkService.$refreshBookmarks).pipe(
      tap(() => {
        this.isLoadingBookmarks = true;
        this.bookmarkService.$bookmarkError.next(false);
      }),
      switchMap(() =>
        this.bookmarkService.getBookmarks(accountId).pipe(
          tap((data: any[]) => this.bookmarkService.$bookmarks.next(data)),
          catchError((err) => {
            console.error("Error fetching bookmarks:", err);
            this.bookmarkService.$bookmarkError.next(true);
            return of([] as any[]);
          })
        )
      ),
      switchMap((bookmarks: any[]) => {
        if (!bookmarks?.length) {
          return of([[], []] as const);
        }

        const detailedBookmarks$ = bookmarks
          .filter(f => f.material && f.serial)
          .map(bookmark =>
              this.dataService.getBookmarkDevice(bookmark.material, bookmark.serial).pipe(
              map(({ response }: ApiResponse<BookmarkDeviceResponse>) => ({
                ...bookmark,
                FUNCTIONAL_LOCATION: response.device?.functional_location,
                ProductFamily: response.device?.product_family,
                system_health: response.system_health,
                service_history: response.service_history,
                totally_down: response.totally_down,
              })),
              catchError(err => {
                console.error('Failed to load bookmark device:', bookmark.serial, err);
                return of(null);
              })
            )
          
          );
        

        const detailedBookmarksSites$ = bookmarks
          .filter(f => f.siteId)
          .map(bookmark =>
            this.dataService.getBookmarkSite(bookmark.siteId).pipe(
              map(({ response }: ApiResponse<BookmarkSiteResponse>) => ({
                ...bookmark,
                location_name: response.functional_location,
                address: response.address,
                city: response.city,
                zip: response.zip,
                country: response.country,
                device_count: response.device_count,
                totally_down_count: response.totally_down_count,
                system_health: response.system_health,
                service_history: response.service_history,
              })),
              catchError(err => {
                console.error('Failed to load bookmark site:', bookmark.siteId, err);
                return of(null);
              })
            )
          );

        const instruments$ = detailedBookmarks$.length > 0 ? forkJoin(detailedBookmarks$) : of([]);
        const sites$ = detailedBookmarksSites$.length > 0 ? forkJoin(detailedBookmarksSites$) : of([]);
        return forkJoin([instruments$, sites$] as const);
      }),
      catchError((err) => {
        console.error("Error loading bookmark details:", err);
        this.bookmarkService.$bookmarkError.next(true);
        return of([[], []] as const);
      })
    ).subscribe(([enrichedBookmarks, enrichedBookmarksSites]) => {
      const config: any[] = this.appConfigService.current;

      const validBookmarks = enrichedBookmarks.filter(b => b !== null);
      const validSites = enrichedBookmarksSites.filter(b => b !== null && b.location_name);

      validBookmarks.forEach(bookmark => {
        bookmark.instrumentName = config.find(f => f?.dataApiName === bookmark.ProductFamily)?.instrumentName;
      });

      validSites.forEach(bookmark => {
        bookmark.addressFull = bookmark.address + ", " + bookmark.city + ", " + bookmark.zip;
      });

      this.bookmarks = validBookmarks.filter(b => b.material && b.serial);
      this.bookmarksSites = validSites.filter(b => b.siteId);

      this.isLoadingBookmarks = false;
    });
  }
    


    this.bookmarkService.$bookmarkError.subscribe((error) => {
      this.isError = error;
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to changed Daterange
    this.daterangeService.$selectedRangeBookmark.subscribe(range => {
      this.selectedRange = range;
    });
  }

  changeGroup(group, event: Event) {

    event.preventDefault();
    event.stopPropagation();
    this.resetFilter();

    this.selectedGroup = group;
  }

  preventClose(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  deleteBookmark(event, bookmark) {

    event.stopPropagation();
    event.preventDefault();

    let body = {
      accountId: this.authService.userData.basic["id"],
      serial: bookmark.serial,
      material: bookmark.material
    }

    this.bookmarkService.deleteBookmark(body).subscribe({
      next: (data) => {
        this.bookmarkService.$refreshBookmarks.next();
      },
      error: (err) => {
        console.error("Error deleting bookmark:", err);
      }
    });
  }

  deleteBookmarkSite(event, bookmark) {

    event.stopPropagation();
    event.preventDefault();

    let body = {
      accountId: this.authService.userData.basic["id"],
      siteId: bookmark.siteId
    }

    this.bookmarkService.deleteBookmarkSite(body).subscribe({
      next: (data) => {
        this.bookmarkService.$refreshBookmarks.next();
      },
      error: (err) => {
        console.error("Error deleting bookmark:", err);
      }
    });
  }


  changeInstrument(bookmark) {

    this.chatService.messages.next([]);
    

    this.selectedSystemService.setNewDevice(bookmark.material, bookmark.serial).subscribe(
      {
        next: (newDevice) => {
          if (!newDevice) {
            return;
          }

          this.bookmarkService.$bookmarksOpen.next(false);
        this.widgetService.resetWidgets();
      }
    });
  }


  changeSite(bookmark) {

    this.chatService.messages.next([]);
    

    this.selectedSiteService.setNewSite(bookmark.siteId).subscribe({
      next: (newSite) => {
        if (!newSite) {
          return;
        }

        this.bookmarkService.$bookmarksOpen.next(false);
        this.widgetService.resetWidgets();
      }
    });
  }


  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'serial':
        case 'instrumentName':
        case 'FUNCTIONAL_LOCATION':
          val1 = a[field]?.toLowerCase() ?? '';
          val2 = b[field]?.toLowerCase() ?? '';
          break;

        case 'autocheck':
          val1 = a.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.autochecks || 0;
          val2 = b.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.autochecks || 0;
          break;
        case 'maintenance':
          val1 = a.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.maintenance || 0;
          val2 = b.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.maintenance || 0;
          break;
        case 'events':
          val1 = a.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.error_events || 0;
          val2 = b.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.error_events || 0;
          break;
        case 'qc':
          val1 = a.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.qc || 0;
          val2 = b.system_health?.metrics?.time_windows?.[this.selectedRange + '']?.qc || 0;
          break;
        case 'noti':
          val1 = a.service_history?.metrics?.time_windows?.[this.selectedRange + '']?.notifications_open || 0;
          val2 = b.service_history?.metrics?.time_windows?.[this.selectedRange + '']?.notifications_open || 0;
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }


  customSortSites(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'siteId': // string field
        case 'address': // string field
        case 'country': // string field
        case 'location_name':
          val1 = a[field]?.toLowerCase() ?? '';
          val2 = b[field]?.toLowerCase() ?? '';
          break;

        case 'autocheck':
          val1 = a.system_health?.time_windows?.[this.selectedRange + '']?.autochecks || 0;
          val2 = b.system_health?.time_windows?.[this.selectedRange + '']?.autochecks || 0;
          break;
        case 'maintenance':
          val1 = a.system_health?.time_windows?.[this.selectedRange + '']?.maintenance || 0;
          val2 = b.system_health?.time_windows?.[this.selectedRange + '']?.maintenance || 0;
          break;
        case 'events':
          val1 = a.system_health?.time_windows?.[this.selectedRange + '']?.error_events || 0;
          val2 = b.system_health?.time_windows?.[this.selectedRange + '']?.error_events || 0;
          break;
        case 'qc':
          val1 = a.system_health?.time_windows?.[this.selectedRange + '']?.qc || 0;
          val2 = b.system_health?.time_windows?.[this.selectedRange + '']?.qc || 0;
          break;
        case 'noti':
          val1 = a.service_history?.time_windows?.[this.selectedRange + '']?.notifications_open || 0;
          val2 = b.service_history?.time_windows?.[this.selectedRange + '']?.notifications_open || 0;
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  changeRange = (range: number, type: 'bookmark') => {
    this.daterangeService.changeRange(range, type);
  };

  refresh() {
    this.bookmarkService.$refreshBookmarks.next();
  }

  resetFilter() {
    this.searchTermSerial = '';
    this.searchTermInstrument = '';
    this.searchTermFuncloc = '';
    this.searchTermName = '';
    this.searchTermAddress = '';
    this.searchTermCountry = '';
    // Reset table filters to show all rows
    if (this.table) {
      this.table.clear();
    }
    if (this.tableSites) {
      this.tableSites.clear();
    }
  }

}
